/**
 * Employee termination + final-pay write path (#23313). Backend allowlist
 * entries — `payroll.terminateEmployee`, `payroll.postGratuityTermination`,
 * `payroll.postPesangon`, plus the read-only preview
 * `payroll.getGratuityProvision` — were added on this same branch; this
 * hook is the first thing that actually calls them.
 *
 * Mirrors `use_employee_mutations.ts`'s write-path shape: `isWriteCapableGrant`
 * (imported from `use_pay_runs.ts`, not re-implemented) gates every mutation,
 * each mutation is pessimistic (state changes only after the resolve), and
 * neither ever throws — every failure resolves a classified
 * `TerminationMutationResult` instead. The four failure buckets are the
 * same idiom used across every other C-child hook here.
 *
 * `getGratuityProvision` preview is exposed through
 * `fetchGratuityProvisionPreview` so the drawer can render the LK
 * settlement preview against the SAME server-derived headcount
 * `postGratuityTermination` uses (#19844); the actual eligibility gate
 * still runs server-side at post time.
 */
import {
  getTrpcClient,
  isBadRequestTrpcError,
  isPayrollAddonRequiredError,
} from '../lib/trpc.js';
import { useAuth } from '../lib/auth/auth_context.js';
import { useOAuthEntityContext } from '../lib/auth/oauth_entity_context.js';
import { isWriteCapableGrant } from './use_pay_runs.js';

/** Same entity-scoping shape as every `payroll.*` call. */
export interface TerminationEntityInput {
  firm_id: string;
  entity_id: string;
}

export interface TerminateEmployeeInput {
  employee_id: string;
  termination_date: string;
  reason?: string;
}

export interface PostGratuityTerminationInput {
  employee_id: string;
  date: string;
  bank_account_id: string;
  ytd_taxable_remuneration_cents: number;
  scheme_approved?: boolean;
}

/**
 * The 24 termination-reason keys the backend's `postPesangon` mutation
 * accepts. Duplicated here as a literal union rather than a dependency on
 * `@ledger/core` (wagepro-web has no direct `@ledger/core` dep in its
 * `package.json` — the tRPC AppRouter's return types are surfaced via
 * `@ledger/ledger-api`'s type-only import, which is why the shared web
 * data hooks don't pull `@ledger/core` in for a pure enum keyset). Kept in
 * lockstep with `PesangonReason` (`packages/jumpstone-core/src/
 * jurisdictions/id/payroll/pesangon.ts`) — a drift is caught at the
 * `client.payroll.postPesangon.mutate` call site, since the tRPC client's
 * input type is derived from `AppRouter` which re-uses this exact enum.
 */
export type PesangonTerminationReason =
  | 'merger_acquisition'
  | 'takeover'
  | 'takeover_worker_refuses_changed_terms'
  | 'efficiency_due_to_losses'
  | 'efficiency_to_prevent_losses'
  | 'closure_due_to_losses'
  | 'closure_not_due_to_losses'
  | 'closure_force_majeure'
  | 'force_majeure_no_closure'
  | 'pkpu_due_to_losses'
  | 'pkpu_not_due_to_losses'
  | 'bankruptcy'
  | 'worker_requested_employer_wrongdoing'
  | 'court_found_no_employer_wrongdoing'
  | 'resignation'
  | 'absence_5_days'
  | 'violation_after_warnings'
  | 'urgent_violation'
  | 'detained_company_loss'
  | 'detained_not_company_loss'
  | 'prolonged_illness_or_disability'
  | 'retirement'
  | 'death';

export interface PostPesangonInput {
  employee_id: string;
  termination_date: string;
  termination_reason: PesangonTerminationReason;
  unused_leave_sen?: number;
  repatriation_sen?: number;
  other_uph_sen?: number;
  separation_pay_sen?: number;
  include_15pct_housing_medical?: boolean;
  pesangon_expense_account_id: string;
  pesangon_payable_account_id: string;
}

/**
 * `already_terminated` is broken out from generic `validation` so the
 * confirm dialog can render an inline "employee is already terminated"
 * message instead of a toast — the "Employee is already terminated" branch
 * of `terminateEmployee`/`postPesangon` in
 * `apps/api/src/trpc/routers/payroll.ts` is not a bug, it is the concurrent
 * -click race the profile screen must survive.
 */
export type TerminationMutationFailure =
  | { kind: 'entitlement_required' }
  | { kind: 'already_terminated' }
  | { kind: 'validation'; message: string }
  | { kind: 'error' };

export type TerminationMutationResult<T = void> =
  | { outcome: 'success'; value: T }
  | { outcome: 'failure'; failure: TerminationMutationFailure };

/**
 * Server-derived gratuity settlement preview for the LK final-pay drawer.
 * `getGratuityProvision` returns this at the ENTITY level (aggregate
 * provision + live headcount) — the per-employee eligibility check runs
 * server-side inside `postGratuityTermination` and is never client-decided.
 */
export interface GratuityProvisionPreview {
  applicable: boolean;
  existing_provision_cents: number;
  required_provision_cents: number;
  provision_delta_cents: number;
  headcount: number;
}

/** The four calls this hook needs, isolated behind an injectable seam. */
export interface TerminationDeps {
  terminateEmployee: (
    input: TerminationEntityInput & TerminateEmployeeInput,
  ) => Promise<{
    ok: boolean;
    employee_id: string;
    ess_access_revoked: boolean;
    ess_access_revocation_failed: boolean;
  }>;
  postGratuityTermination: (
    input: TerminationEntityInput & PostGratuityTerminationInput,
  ) => Promise<{
    provision_release_cents: number;
    cash_payment_cents: number;
    pnl_true_up_cents: number;
    retention_cents: number;
    journal_id: string;
  }>;
  postPesangon: (
    input: TerminationEntityInput & PostPesangonInput,
  ) => Promise<{ total_sen: number }>;
  getGratuityProvision: (
    input: TerminationEntityInput & { as_of_date: string },
  ) => Promise<GratuityProvisionPreview>;
}

function defaultTerminationDeps(): TerminationDeps {
  const client = getTrpcClient();
  return {
    terminateEmployee: (input) =>
      client.payroll.terminateEmployee.mutate(input),
    postGratuityTermination: (input) =>
      client.payroll.postGratuityTermination.mutate(input),
    postPesangon: (input) => client.payroll.postPesangon.mutate(input),
    getGratuityProvision: (input) =>
      client.payroll.getGratuityProvision.query(input),
  };
}

/**
 * The backend's "already terminated" BAD_REQUEST — matched by verbatim
 * message (`terminateEmployee` and `postPesangon` both throw this exact
 * string, `apps/api/src/trpc/routers/payroll.ts`). Kept as a constant so a
 * single grep finds every dependent site if the backend message is ever
 * changed. Safer than a substring match: a future BAD_REQUEST with the
 * unrelated substring "terminated" would not misclassify here.
 */
const ALREADY_TERMINATED_MESSAGE = 'Employee is already terminated';

function classifyTerminationFailure(err: unknown): TerminationMutationFailure {
  if (isPayrollAddonRequiredError(err)) {
    return { kind: 'entitlement_required' };
  }
  if (isBadRequestTrpcError(err)) {
    const message = err instanceof Error ? err.message : '';
    if (message === ALREADY_TERMINATED_MESSAGE) {
      return { kind: 'already_terminated' };
    }
    return { kind: 'validation', message };
  }
  return { kind: 'error' };
}

export async function terminateEmployeeRecord(
  input: TerminateEmployeeInput,
  entityInput: TerminationEntityInput,
  deps?: TerminationDeps,
): Promise<TerminationMutationResult<{ employee_id: string }>> {
  try {
    const resolvedDeps = deps ?? defaultTerminationDeps();
    const result = await resolvedDeps.terminateEmployee({
      ...entityInput,
      ...input,
    });
    return {
      outcome: 'success',
      value: { employee_id: result.employee_id },
    };
  } catch (err) {
    console.error('[wagepro-web] terminateEmployee failed:', err);
    return {
      outcome: 'failure',
      failure: classifyTerminationFailure(err),
    };
  }
}

export async function postGratuityTerminationRecord(
  input: PostGratuityTerminationInput,
  entityInput: TerminationEntityInput,
  deps?: TerminationDeps,
): Promise<
  TerminationMutationResult<{
    provision_release_cents: number;
    cash_payment_cents: number;
    pnl_true_up_cents: number;
    retention_cents: number;
    journal_id: string;
  }>
> {
  try {
    const resolvedDeps = deps ?? defaultTerminationDeps();
    const result = await resolvedDeps.postGratuityTermination({
      ...entityInput,
      ...input,
    });
    return { outcome: 'success', value: result };
  } catch (err) {
    console.error('[wagepro-web] postGratuityTermination failed:', err);
    return {
      outcome: 'failure',
      failure: classifyTerminationFailure(err),
    };
  }
}

export async function postPesangonRecord(
  input: PostPesangonInput,
  entityInput: TerminationEntityInput,
  deps?: TerminationDeps,
): Promise<TerminationMutationResult<{ total_sen: number }>> {
  try {
    const resolvedDeps = deps ?? defaultTerminationDeps();
    const result = await resolvedDeps.postPesangon({
      ...entityInput,
      ...input,
    });
    return { outcome: 'success', value: result };
  } catch (err) {
    console.error('[wagepro-web] postPesangon failed:', err);
    return {
      outcome: 'failure',
      failure: classifyTerminationFailure(err),
    };
  }
}

export type FetchGratuityProvisionResult =
  | { outcome: 'success'; value: GratuityProvisionPreview }
  | { outcome: 'failure'; failure: TerminationMutationFailure };

export async function fetchGratuityProvisionPreview(
  input: { as_of_date: string },
  entityInput: TerminationEntityInput,
  deps?: TerminationDeps,
): Promise<FetchGratuityProvisionResult> {
  try {
    const resolvedDeps = deps ?? defaultTerminationDeps();
    const value = await resolvedDeps.getGratuityProvision({
      ...entityInput,
      ...input,
    });
    return { outcome: 'success', value };
  } catch (err) {
    console.error('[wagepro-web] getGratuityProvision failed:', err);
    return {
      outcome: 'failure',
      failure: classifyTerminationFailure(err),
    };
  }
}

export interface UseTerminationResult {
  terminate:
    | ((
        input: TerminateEmployeeInput,
      ) => Promise<TerminationMutationResult<{ employee_id: string }>>)
    | undefined;
  settle_gratuity:
    | ((input: PostGratuityTerminationInput) => Promise<
        TerminationMutationResult<{
          provision_release_cents: number;
          cash_payment_cents: number;
          pnl_true_up_cents: number;
          retention_cents: number;
          journal_id: string;
        }>
      >)
    | undefined;
  post_pesangon:
    | ((
        input: PostPesangonInput,
      ) => Promise<TerminationMutationResult<{ total_sen: number }>>)
    | undefined;
  fetch_gratuity_provision:
    | ((input: { as_of_date: string }) => Promise<FetchGratuityProvisionResult>)
    | undefined;
  /** `true` when the OAuth grant is NOT write-capable — the terminate affordance must be hidden entirely (not just disabled). */
  is_read_only_grant: boolean;
}

/**
 * Termination + final-pay write path, gated identically to
 * `use_employee_mutations.ts`: reachable only with a bound entity AND a
 * write-capable OAuth grant. `fetch_gratuity_provision` (a query) is
 * available whenever the entity is bound, since a read-only token can still
 * PREVIEW the provision even if it can't post the settlement.
 */
export function useTermination(): UseTerminationResult {
  const entityContext = useOAuthEntityContext();
  const { grant } = useAuth();

  const readyEntity = entityContext.status === 'ready' ? entityContext : null;
  const canWrite = readyEntity !== null && isWriteCapableGrant(grant);
  const canRead = readyEntity !== null;

  const terminate = canWrite
    ? (input: TerminateEmployeeInput) =>
        terminateEmployeeRecord(input, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        })
    : undefined;

  const settle_gratuity = canWrite
    ? (input: PostGratuityTerminationInput) =>
        postGratuityTerminationRecord(input, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        })
    : undefined;

  const post_pesangon = canWrite
    ? (input: PostPesangonInput) =>
        postPesangonRecord(input, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        })
    : undefined;

  const fetch_gratuity_provision = canRead
    ? (input: { as_of_date: string }) =>
        fetchGratuityProvisionPreview(input, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        })
    : undefined;

  return {
    terminate,
    settle_gratuity,
    post_pesangon,
    fetch_gratuity_provision,
    is_read_only_grant: !isWriteCapableGrant(grant),
  };
}
